import { useContext, useState } from "react";
import type { PanelLayoutChoice } from "_shared/_views/panels/_api/panel-layout-preferences-api";
import { PanelLayoutPreferencesContext } from "_shared/_views/panels/panel-layout-preferences-context";

const NO_CHOICE: PanelLayoutChoice = { width: null, isCollapsed: false };

/**
 * How the reader keeps one panel, and a way to change it (#6621).
 *
 * The change shows at once and is saved to the reader's account, so the panel opens the same way in
 * the next tab, after a reload and on another device. Without a provider above it, or before the
 * server has answered, the panel opens as for a reader who never chose, and still keeps a choice made
 * on this page for as long as it is on screen.
 */
export function usePanelLayoutPreference(panelKey: string) {
  const preferences = useContext(PanelLayoutPreferencesContext);
  const [localChoice, setLocalChoice] = useState<PanelLayoutChoice>();
  const choice = localChoice ?? preferences?.savedPanels?.get(panelKey) ?? NO_CHOICE;

  function update(change: Partial<PanelLayoutChoice>) {
    const next: PanelLayoutChoice = { ...choice, ...change };
    setLocalChoice(next);
    preferences?.savePanel(panelKey, next);
  }

  return {
    width: choice.width ?? undefined,
    isCollapsed: choice.isCollapsed,
    setWidth: (width: number) => update({ width: Math.round(width) }),
    setCollapsed: (isCollapsed: boolean) => update({ isCollapsed }),
  };
}
